import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import {
  ArrowLeft, CalendarDays, Clock, MapPin, Minus, Plus, ScrollText,
  ShieldCheck, ExternalLink, Loader2,
} from 'lucide-react';
import { getBackendUrl } from '../services/origin';
import { PayPalLogo } from '../components/PayPalLogo';
import { CardBrands } from '../components/CardBrands';
import CookieConsentBanner from '../components/CookieConsentBanner';
import CookieFooterLink from '../components/CookieFooterLink';
import { loadTracking, trackEvent } from '../services/tracking';

const API = getBackendUrl();

/**
 * Public event checkout page. URL: `/event/<slug>/checkout`.
 *
 * Flow:
 *   1. Hydrate event/tiers/branding from `/api/public/events/<slug>/checkout`
 *      (also honors `?dt=<dashboard_token>` for prefill from authenticated PWA).
 *   2. User picks quantities per tier, fills name/surname/email/phone, accepts T&C.
 *   3. POST `/create-order` → returns Stripe Checkout URL → window.location.assign.
 *   4. Stripe redirects back to `?o=<order_token>` success page.
 *
 * Optional checkbox: "Iscrivimi a Lazo" — auto-registers customer with the
 * usual self-registration flow (PIN+SMS+welcome bonus) on payment confirm.
 */
export default function EventCheckoutPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const dt = searchParams.get('dt') || '';
  const cancelled = searchParams.get('cancelled') === '1';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [qty, setQty] = useState({}); // { tier_name: number }
  const [tcOk, setTcOk] = useState(false);
  const [privacyOk, setPrivacyOk] = useState(false);
  const [marketingOk, setMarketingOk] = useState(false);
  const [lazoOk, setLazoOk] = useState(true);  // Pre-checked by default — best UX for non-members

  const [submitting, setSubmitting] = useState(false);
  const [submittingMethod, setSubmittingMethod] = useState(null); // 'stripe' | 'paypal' | null

  // Real-time "already enrolled" detection — fires after the buyer has typed
  // BOTH an email and a phone (or arrives prefilled). Lets us hide the
  // "Iscriviti al programma" checkbox + show a friendly badge instead, so
  // existing members don't accidentally trigger a double enrollment.
  const [memberCheck, setMemberCheck] = useState({ checked: false, is_member: false, first_name: '' });

  useEffect(() => {
    document.title = data?.business_name ? `Checkout biglietti — ${data.business_name}` : 'Checkout biglietti';
  }, [data?.business_name]);

  useEffect(() => {
    // Skip when arriving from PWA (data.is_member is already authoritative).
    if (data?.is_member) {
      setMemberCheck({ checked: true, is_member: true, first_name: data.prefill?.first_name || '' });
      return;
    }
    const ph = (phone || '').trim();
    const em = (email || '').trim();
    const emailOk = /^[^@]+@[^@]+\.[^@]+$/.test(em);
    const phoneOk = ph.replace(/\D/g, '').length >= 7;
    if (!emailOk && !phoneOk) {
      setMemberCheck({ checked: false, is_member: false, first_name: '' });
      return;
    }
    const handle = setTimeout(() => {
      axios.get(`${API}/api/public/check-member`, {
        params: { slug, phone: ph, email: em.toLowerCase() },
      })
        .then(r => setMemberCheck({ checked: true, is_member: !!r.data?.is_member, first_name: r.data?.first_name || '' }))
        .catch(() => setMemberCheck({ checked: true, is_member: false, first_name: '' }));
    }, 600);
    return () => clearTimeout(handle);
  }, [phone, email, slug, data?.is_member, data?.prefill]);

  // If we detect the user is already a member, never send loyalty opt-in.
  const isMember = data?.is_member || memberCheck.is_member;
  const showLoyaltyCheckbox = !isMember;

  useEffect(() => {
    axios.get(`${API}/api/public/events/${slug}/checkout`, {
      params: dt ? { dt } : {},
    })
      .then(r => {
        setData(r.data);
        if (r.data.prefill) {
          setFirstName(r.data.prefill.first_name || '');
          setLastName(r.data.prefill.last_name || '');
          setPhone(r.data.prefill.phone || '');
          setEmail(r.data.prefill.email || '');
        }
        // Tracking: load pixels + fire view_item + begin_checkout
        // Use the server-generated event_ids so the parallel CAPI calls
        // (Meta/TikTok/GA4) can deduplicate browser+server events.
        if (r.data.brand_id) {
          loadTracking(r.data.brand_id).then(() => {
            const items = (r.data.tiers || []).map(t => ({
              item_id: `${r.data.event?.id}:${t.name}`,
              item_name: t.name,
              item_category: r.data.event?.name || 'Event ticket',
              price: t.price,
              quantity: 1,
            }));
            trackEvent(r.data.brand_id, 'view_item', {
              currency: 'EUR',
              value: 0,
              items,
              event_id: r.data.view_item_event_id,
            });
            trackEvent(r.data.brand_id, 'begin_checkout', {
              currency: 'EUR',
              value: 0,
              items,
              event_id: r.data.begin_checkout_event_id,
            });
          });
        }
      })
      .catch(err => setError(err.response?.data?.detail || 'Evento non trovato'))
      .finally(() => setLoading(false));
  }, [slug, dt]);

  const total = useMemo(() => {
    if (!data?.tiers) return 0;
    return data.tiers.reduce((sum, t) => sum + (qty[t.name] || 0) * t.price, 0);
  }, [qty, data]);

  const totalTickets = useMemo(
    () => Object.values(qty).reduce((s, n) => s + (n || 0), 0),
    [qty],
  );

  const branding = data?.branding || {};
  const pc = branding.primary_color || '#D44E20';
  const sc = branding.secondary_color || '#3ECF8E';

  const setQ = (tier, delta, max) => {
    setQty(q => {
      const cur = q[tier] || 0;
      const next = Math.max(0, cur + delta);
      const limited = max != null ? Math.min(max, next) : next;
      return { ...q, [tier]: limited };
    });
  };

  const buildPayload = () => {
    const lines = Object.entries(qty)
      .filter(([, n]) => n > 0)
      .map(([tier_name, quantity]) => ({ tier_name, quantity }));
    return {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      phone: phone.trim(),
      email: email.trim().toLowerCase(),
      lines,
      tc_accepted: tcOk,
      privacy_accepted: privacyOk,
      marketing_opt_in: marketingOk,
      lazo_opt_in: lazoOk && showLoyaltyCheckbox,
      dashboard_token: dt || null,
    };
  };

  const validate = () => {
    if (!firstName.trim()) { toast.error('Nome obbligatorio'); return false; }
    if (!lastName.trim()) { toast.error('Cognome obbligatorio'); return false; }
    if (!phone.trim() || phone.replace(/\D/g, '').length < 7) { toast.error('Telefono non valido'); return false; }
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(email.trim())) { toast.error('Email non valida'); return false; }
    if (!tcOk || !privacyOk) { toast.error('Devi accettare T&C e Privacy'); return false; }
    if (totalTickets === 0) { toast.error('Seleziona almeno un biglietto'); return false; }
    return true;
  };

  // Best-effort: tracking errors must never block the checkout flow.
  const fireAddPaymentInfo = (method, eventId) => {
    try {
      if (!data?.brand_id) return;
      const items = (data.tiers || [])
        .filter(t => (qty[t.name] || 0) > 0)
        .map(t => ({
          item_id: `${data.event?.id}:${t.name}`,
          item_name: t.name,
          item_category: data.event?.name || 'Event ticket',
          price: t.price,
          quantity: qty[t.name] || 0,
        }));
      trackEvent(data.brand_id, 'add_payment_info', {
        currency: 'EUR',
        value: total,
        payment_type: method,
        items,
        // event_id must match the server-side CAPI mirror so Meta/TikTok
        // deduplicate browser+server events. The server uses order_token.
        event_id: eventId,
      });
    } catch (e) {
      // swallow tracking errors so the user can always proceed to payment
      console.warn('[tracking] add_payment_info failed', e);
    }
  };

  const handleStripeSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmittingMethod('stripe');
    try {
      const res = await axios.post(`${API}/api/public/events/${slug}/create-order`, buildPayload());
      // Fire add_payment_info pixel AFTER backend created the order, so we
      // can pass the order_token as event_id for CAPI dedup.
      fireAddPaymentInfo('stripe', res.data?.order_token);
      if (res.data?.stripe_url) {
        window.location.assign(res.data.stripe_url);
      } else {
        throw new Error('Errore nella creazione dell\'ordine');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Errore');
      setSubmitting(false);
      setSubmittingMethod(null);
    }
  };

  const handlePayPalSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmittingMethod('paypal');
    try {
      const res = await axios.post(`${API}/api/public/events/${slug}/create-order-paypal`, buildPayload());
      fireAddPaymentInfo('paypal', res.data?.order_token);
      if (res.data?.paypal_approval_url) {
        window.location.assign(res.data.paypal_approval_url);
      } else {
        throw new Error('Errore nella creazione dell\'ordine PayPal');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Errore PayPal');
      setSubmitting(false);
      setSubmittingMethod(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 className="animate-spin" style={{ color: pc }} size={28} />
      </div>
    );
  }

  if (error || !data?.event) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center p-6">
        <p className="text-base font-bold text-[#1A1A1A]">Evento non disponibile</p>
        <p className="text-sm text-[#64748B] mt-1">{error}</p>
      </div>
    );
  }

  const ev = data.event;

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24" data-testid="event-checkout-page">
      <Toaster position="top-center" />
      <CookieConsentBanner brandId={data.brand_id} />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => window.history.back()} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="checkout-back-btn">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8]">Checkout · {data.business_name}</p>
            <p className="text-sm font-bold text-[#1A1A1A] truncate">Biglietti — {ev.name}</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {cancelled && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="cancelled-banner">
            Pagamento annullato. Puoi riprovare quando vuoi.
          </div>
        )}

        {/* Event card */}
        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden" data-testid="event-summary-card">
          {ev.image_url && (
            <img src={ev.image_url} alt={ev.name} className="w-full h-40 object-cover" />
          )}
          <div className="p-4">
            <h1 className="text-xl font-bold text-[#1A1A1A]">{ev.name}</h1>
            {ev.description && (
              <p className="text-sm text-[#64748B] mt-1">{ev.description}</p>
            )}
            <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-[#475569]">
              {ev.event_date && (
                <span className="flex items-center gap-1.5"><CalendarDays size={13} style={{ color: pc }} /> {ev.event_date}</span>
              )}
              {ev.event_time && (
                <span className="flex items-center gap-1.5"><Clock size={13} style={{ color: pc }} /> {ev.event_time}</span>
              )}
              {ev.location && (
                <span className="col-span-2 flex items-center gap-1.5"><MapPin size={13} style={{ color: pc }} /> {ev.location}</span>
              )}
            </div>
          </div>
        </div>

        {/* Tier picker */}
        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4" data-testid="tier-picker">
          <h2 className="text-sm font-bold text-[#1A1A1A] mb-3">Scegli i biglietti</h2>
          <div className="space-y-2">
            {data.tiers.map((t) => {
              const n = qty[t.name] || 0;
              const max = ev.max_tickets_per_customer || (t.available != null ? t.available : 99);
              return (
                <div key={t.name} data-testid={`tier-${t.name}`}
                  className={`rounded-xl border p-3 flex items-center gap-3 ${t.sold_out ? 'opacity-50' : ''}`}
                  style={{ borderColor: n > 0 ? pc : '#E5E7EB', backgroundColor: n > 0 ? `${pc}06` : '#FFF' }}>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-[#1A1A1A]">{t.name}</p>
                    {t.description && <p className="text-xs text-[#64748B] mt-0.5">{t.description}</p>}
                    <p className="text-base font-bold mt-1" style={{ color: pc }}>{t.price.toFixed(2).replace('.', ',')} €</p>
                    {t.available != null && t.available <= 10 && !t.sold_out && (
                      <p className="text-[10px] mt-0.5 text-amber-600 font-semibold">Solo {t.available} disponibili</p>
                    )}
                    {t.sold_out && <p className="text-[10px] mt-0.5 text-red-600 font-bold">Sold out</p>}
                  </div>
                  {!t.sold_out && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => setQ(t.name, -1, max)} disabled={n === 0}
                        data-testid={`tier-${t.name}-minus`}
                        className="w-8 h-8 rounded-full bg-slate-100 disabled:opacity-30 flex items-center justify-center active:scale-90">
                        <Minus size={14} />
                      </button>
                      <span className="w-6 text-center font-bold text-sm tabular-nums" data-testid={`tier-${t.name}-qty`}>{n}</span>
                      <button onClick={() => setQ(t.name, 1, max)} disabled={n >= max}
                        data-testid={`tier-${t.name}-plus`}
                        className="w-8 h-8 rounded-full text-white disabled:opacity-30 flex items-center justify-center active:scale-90"
                        style={{ backgroundColor: pc }}>
                        <Plus size={14} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Buyer form */}
        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4" data-testid="buyer-form">
          <h2 className="text-sm font-bold text-[#1A1A1A] mb-3">I tuoi dati</h2>
          {data.is_member && (
            <p className="text-xs text-[#64748B] mb-3 -mt-2 flex items-center gap-1.5">
              <ShieldCheck size={12} style={{ color: sc }} /> Sei riconosciuto. Dati pre-compilati dalla tua bacheca.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium text-[#64748B] block mb-1">Nome*</label>
              <input value={firstName} onChange={e => setFirstName(e.target.value)} data-testid="first-name-input"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#64748B] block mb-1">Cognome*</label>
              <input value={lastName} onChange={e => setLastName(e.target.value)} data-testid="last-name-input"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] font-medium text-[#64748B] block mb-1">Email*</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="email-input"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] font-medium text-[#64748B] block mb-1">Telefono*</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} data-testid="phone-input"
                placeholder="+39 333 1234567"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
          </div>
        </div>

        {/* Consents */}
        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4" data-testid="consents">
          <Checkbox checked={tcOk} onChange={setTcOk} testid="tc-checkbox" pc={pc}>
            Ho letto e accetto i <a href={`${data.tc_url}`} target="_blank" rel="noopener noreferrer" className="font-semibold underline" style={{ color: pc }}>Termini e Condizioni</a>*
          </Checkbox>
          <Checkbox checked={privacyOk} onChange={setPrivacyOk} testid="privacy-checkbox" pc={pc}>
            Ho letto e accetto la <a href={data.privacy_url} target="_blank" rel="noopener noreferrer" className="font-semibold underline" style={{ color: pc }}>Privacy Policy</a>* <span className="inline-flex align-middle ml-1 text-[#94A3B8]"><CookieFooterLink className="text-[#94A3B8]" /></span>
          </Checkbox>
          <Checkbox checked={marketingOk} onChange={setMarketingOk} testid="marketing-checkbox" pc={pc}>
            Acconsento a ricevere comunicazioni di marketing su prossimi eventi e offerte (facoltativo)
          </Checkbox>
          {showLoyaltyCheckbox && (
            <>
              <Checkbox checked={lazoOk} onChange={setLazoOk} testid="lazo-optin-checkbox" pc={pc}>
                <strong>Iscrivimi al {data.program_name || 'programma fedeltà'} di {data.business_name}</strong> per guadagnare punti e ottenere premi e sconti esclusivi. Riceverai un SMS con il PIN per la tua bacheca digitale.
              </Checkbox>
              <p className="text-[10px] text-[#94A3B8] mt-1 ml-7 leading-relaxed" data-testid="loyalty-disclaimer">
                Se sei già iscritto al programma, lascia deselezionato per evitare doppi messaggi.
              </p>
            </>
          )}
          {isMember && (
            <div className="mt-2 flex items-start gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="already-member-banner">
              <span className="text-emerald-600 text-base leading-none">✓</span>
              <p className="text-[12px] text-emerald-900 leading-snug">
                {memberCheck.first_name ? `Ciao ${memberCheck.first_name}, sei già iscritto` : 'Sei già iscritto'} al {data.program_name || 'programma fedeltà'} di <b>{data.business_name}</b>. I punti di questo acquisto saranno accreditati automaticamente al tuo account.
              </p>
            </div>
          )}
        </div>

        {/* Order summary */}
        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4" data-testid="order-summary">
          <h2 className="text-sm font-bold text-[#1A1A1A] mb-3">Riepilogo</h2>
          {data.tiers.map(t => {
            const n = qty[t.name] || 0;
            if (n === 0) return null;
            return (
              <div key={t.name} className="flex justify-between text-sm py-1">
                <span className="text-[#475569]">{n}× {t.name}</span>
                <span className="font-medium text-[#1A1A1A] tabular-nums">{(n * t.price).toFixed(2).replace('.', ',')} €</span>
              </div>
            );
          })}
          <div className="border-t border-slate-100 mt-2 pt-3 flex justify-between">
            <span className="font-bold text-base text-[#1A1A1A]">Totale</span>
            <span className="font-black text-lg tabular-nums" style={{ color: pc }} data-testid="total-amount">{total.toFixed(2).replace('.', ',')} €</span>
          </div>
        </div>
      </main>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Da pagare</p>
            <p className="text-lg font-black tabular-nums" style={{ color: pc }}>
              {total.toFixed(2).replace('.', ',')} €
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={handleStripeSubmit}
              disabled={submitting || total <= 0 || !tcOk || !privacyOk}
              data-testid="submit-checkout-btn"
              className="h-11 px-3 rounded-2xl text-white text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-40"
              style={{ backgroundColor: pc }}>
              {submittingMethod === 'stripe'
                ? <><Loader2 size={14} className="animate-spin" />Reindirizzamento…</>
                : <>Paga con carta<ExternalLink size={13} /></>}
            </button>
            <button onClick={handlePayPalSubmit}
              disabled={submitting || total <= 0 || !tcOk || !privacyOk}
              data-testid="submit-checkout-paypal-btn"
              className="h-11 px-3 rounded-2xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-40"
              style={{ backgroundColor: '#FFC439' }}>
              {submittingMethod === 'paypal'
                ? <><Loader2 size={14} className="animate-spin" />Reindirizzamento…</>
                : <PayPalLogo height={20} />}
            </button>
          </div>
          <CardBrands className="mt-2" />
        </div>
        <div className="h-[env(safe-area-inset-bottom)] bg-white" />
      </div>
    </div>
  );
}

function Checkbox({ checked, onChange, children, testid, pc }) {
  return (
    <label className="flex items-start gap-3 py-2 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        data-testid={testid}
        className="mt-1 w-4 h-4 rounded accent-[#D44E20] flex-shrink-0"
        style={{ accentColor: pc }} />
      <span className="text-xs text-[#475569] leading-relaxed">{children}</span>
    </label>
  );
}
